import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { renderDenseIcon } from '../../presentation/cards/iconRegistry';
import type { DenseCardModel, DenseIconKey, DenseTagModel, DenseTone } from '../../presentation/cards/types';

interface DenseEntityCardBodyProps {
  model: DenseCardModel;
  leading?: React.ReactNode;
  actions?: React.ReactNode;
  metricLayout?: 'grid' | 'row';
  titleIcon?: DenseIconKey;
  compactTitle?: boolean;
  inlineTitleWithLeading?: boolean;
  density?: 'regular' | 'compact';
  singleLineHighlightTags?: boolean;
  singleLinePrimaryMetrics?: boolean;
  singleLineCapacityTags?: boolean;
  hideMetricDetails?: boolean;
  wrapSubtitle?: boolean;
  contentOrder?: Array<'highlightTags' | 'primaryMetrics' | 'capacityTags' | 'breachTags' | 'footer'>;
}

const toneConfig: Record<DenseTone, { base: string; text: string }> = {
  neutral: { base: '#64748b', text: '#334155' },
  info: { base: '#2563eb', text: '#1d4ed8' },
  accent: { base: '#4f46e5', text: '#4338ca' },
  good: { base: '#0f766e', text: '#0f766e' },
  warn: { base: '#b45309', text: '#b45309' },
  danger: { base: '#b42318', text: '#b42318' },
};

const DenseEntityCardBody: React.FC<DenseEntityCardBodyProps> = ({
  model,
  leading,
  actions,
  metricLayout = 'grid',
  titleIcon,
  compactTitle = false,
  inlineTitleWithLeading = false,
  density = 'regular',
  singleLineHighlightTags = false,
  singleLinePrimaryMetrics = false,
  singleLineCapacityTags = false,
  hideMetricDetails = false,
  wrapSubtitle = false,
  contentOrder = ['highlightTags', 'primaryMetrics', 'capacityTags', 'breachTags', 'footer'],
}) => {
  const theme = useTheme();
  const isCompact = density === 'compact';
  const titleFontWeight = compactTitle ? 500 : isCompact ? 600 : 700;
  const titleFontSize = compactTitle ? 13.25 : isCompact ? 15 : undefined;
  const sectionGap = isCompact ? 0.9 : 1.15;
  const inlineGap = isCompact ? 0.55 : 0.75;

  const titleBlock = (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: titleIcon ? 0.55 : 0, minWidth: 0 }}>
        {titleIcon ? (
          <Box
            sx={{
              width: compactTitle ? 18 : 20,
              height: compactTitle ? 18 : 20,
              borderRadius: 1.1,
              border: '1px solid',
              borderColor: alpha(theme.palette.text.primary, 0.12),
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              color: theme.palette.text.secondary,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {renderDenseIcon(titleIcon, { sx: { fontSize: compactTitle ? 11.5 : 13, color: 'inherit' } })}
          </Box>
        ) : null}
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: titleFontSize,
            fontWeight: titleFontWeight,
            letterSpacing: compactTitle || isCompact ? 0 : -0.1,
            lineHeight: compactTitle ? 1.08 : isCompact ? 1.12 : 1.14,
            color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {model.title}
        </Typography>
      </Box>
      {model.subtitle ? (
        <Typography
          variant="body2"
          sx={{
            mt: compactTitle ? 0.2 : 0.45,
            ml: titleIcon && !inlineTitleWithLeading ? (compactTitle ? 2.95 : 3.2) : 0,
            color: theme.palette.text.secondary,
            lineHeight: compactTitle ? 1.18 : isCompact ? 1.26 : 1.42,
            fontSize: compactTitle ? 11.25 : isCompact ? 11.5 : undefined,
            fontWeight: 400,
            minWidth: 0,
            overflow: wrapSubtitle ? 'visible' : 'hidden',
            textOverflow: wrapSubtitle ? 'clip' : 'ellipsis',
            whiteSpace: wrapSubtitle ? 'normal' : 'nowrap',
            overflowWrap: wrapSubtitle ? 'anywhere' : 'normal',
          }}
        >
          {model.subtitle}
        </Typography>
      ) : null}
    </Box>
  );

  const renderTag = (
    tag: DenseTagModel,
    options: { stretch?: boolean; forceTooltip?: boolean } = {}
  ) => {
    const tone = toneConfig[tag.tone ?? 'neutral'];
    const emphasis = tag.emphasis ?? 'soft';
    const backgroundAlpha = emphasis === 'strong' ? (isCompact ? 0.11 : 0.16) : emphasis === 'micro' ? 0.05 : 0.08;
    const borderAlpha = emphasis === 'strong' ? 0.24 : 0.16;
    const fontSize = emphasis === 'micro' ? (isCompact ? 10.25 : 11) : isCompact ? 11.25 : 12.5;
    const content = (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: options.stretch ? 'flex-start' : 'center',
          gap: isCompact ? 0.4 : 0.55,
          px: emphasis === 'micro' ? (isCompact ? 0.5 : 0.7) : isCompact ? 0.68 : 0.9,
          py: emphasis === 'micro' ? (isCompact ? 0.28 : 0.4) : isCompact ? 0.38 : 0.55,
          borderRadius: emphasis === 'micro' ? 1.25 : isCompact ? 1.55 : 1.8,
          border: '1px solid',
          borderColor: alpha(tone.base, borderAlpha),
          bgcolor: alpha(tone.base, backgroundAlpha),
          color: tone.text,
          minHeight: emphasis === 'micro' ? (isCompact ? 20 : 24) : isCompact ? 23 : 28,
          width: options.stretch ? '100%' : undefined,
          minWidth: 0,
          maxWidth: options.stretch ? '100%' : tag.maxWidth,
          overflow: 'hidden',
        }}
      >
        {renderDenseIcon(tag.icon, {
          sx: {
            fontSize: emphasis === 'micro' ? (isCompact ? 11.5 : 13) : isCompact ? 12.5 : 15,
            color: 'inherit',
            flexShrink: 0,
          },
        })}
        <Typography
          component="span"
          sx={{
            fontSize,
            fontWeight: emphasis === 'strong' ? 600 : 500,
            lineHeight: 1,
            letterSpacing: 0,
            whiteSpace: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {tag.label}
        </Typography>
      </Box>
    );

    const tooltipTitle = tag.tooltip ?? (options.forceTooltip ? tag.label : undefined);
    if (!tooltipTitle) {
      return <React.Fragment key={tag.key}>{content}</React.Fragment>;
    }

    return (
      <Tooltip key={tag.key} title={tooltipTitle}>
        {content}
      </Tooltip>
    );
  };

  const renderTagRow = (
    tags: DenseTagModel[],
    options: { singleLine?: boolean } = {}
  ) => {
    if (!tags.length) return null;

    if (options.singleLine) {
      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${tags.length}, minmax(0, 1fr))`,
            gap: isCompact ? 0.55 : 0.7,
            minWidth: 0,
          }}
        >
          {tags.map((tag) => renderTag(tag, { stretch: true, forceTooltip: true }))}
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isCompact ? 0.55 : 0.7 }}>
        {tags.map((tag) => renderTag(tag))}
      </Box>
    );
  };

  const contentSections: Record<'highlightTags' | 'primaryMetrics' | 'capacityTags' | 'breachTags' | 'footer', React.ReactNode> = {
    highlightTags: renderTagRow(model.highlightTags, { singleLine: singleLineHighlightTags }),
    primaryMetrics: model.primaryMetrics.length > 0 ? (
      <Box
        sx={{
          display: metricLayout === 'row' && singleLinePrimaryMetrics ? 'grid' : metricLayout === 'row' ? 'flex' : 'grid',
          flexWrap: metricLayout === 'row' && !singleLinePrimaryMetrics ? 'wrap' : undefined,
          alignItems: metricLayout === 'row' ? 'center' : undefined,
          gridTemplateColumns:
            metricLayout === 'row' && singleLinePrimaryMetrics
              ? `repeat(${model.primaryMetrics.length}, minmax(0, 1fr))`
              : metricLayout === 'grid'
              ? {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(auto-fit, minmax(118px, 1fr))',
                }
              : undefined,
          gap: metricLayout === 'row' ? (isCompact ? 0.7 : 1.1) : isCompact ? 0.8 : 0.95,
          minWidth: 0,
        }}
      >
        {model.primaryMetrics.map((metric) => {
          const tone = toneConfig[metric.tone ?? 'neutral'];
          const metricTooltip =
            metric.tooltip ??
            (singleLinePrimaryMetrics
              ? [metric.value, metric.unit, !hideMetricDetails ? metric.detail : undefined].filter(Boolean).join(' ')
              : undefined);
          const showMetricDetail = Boolean(metric.detail) && !hideMetricDetails && !singleLinePrimaryMetrics;
          const content = (
            <Box
              key={metric.key}
              sx={{
                display: 'flex',
                alignItems: metricLayout === 'row' ? 'center' : 'flex-start',
                gap: metricLayout === 'row' ? (isCompact ? 0.45 : 0.6) : isCompact ? 0.55 : 0.8,
                minWidth: 0,
                width: singleLinePrimaryMetrics ? '100%' : undefined,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  mt: metricLayout === 'row' ? 0 : 0.1,
                  width: metricLayout === 'row' ? (isCompact ? 18 : 22) : isCompact ? 22 : 26,
                  height: metricLayout === 'row' ? (isCompact ? 18 : 22) : isCompact ? 22 : 26,
                  borderRadius: isCompact ? 1.1 : 1.4,
                  border: '1px solid',
                  borderColor: alpha(tone.base, 0.16),
                  bgcolor: alpha(tone.base, isCompact ? 0.06 : 0.08),
                  color: tone.text,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {renderDenseIcon(metric.icon, {
                  sx: {
                    fontSize: metricLayout === 'row' ? (isCompact ? 11 : 13) : isCompact ? 13 : 15,
                    color: 'inherit',
                  },
                })}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    display: 'block',
                    fontSize: metricLayout === 'row' ? (isCompact ? 12 : 13.5) : isCompact ? 13 : 14.5,
                    fontWeight: 500,
                    lineHeight: 1.1,
                    color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
                    minWidth: 0,
                    whiteSpace: singleLinePrimaryMetrics ? 'nowrap' : 'normal',
                    overflow: singleLinePrimaryMetrics ? 'hidden' : 'visible',
                    textOverflow: singleLinePrimaryMetrics ? 'ellipsis' : 'clip',
                    overflowWrap: singleLinePrimaryMetrics ? 'normal' : 'anywhere',
                  }}
                >
                  {metric.value}
                  {metric.unit ? (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.28,
                        fontSize: metricLayout === 'row' ? (isCompact ? 9.5 : 10.5) : isCompact ? 10.25 : 11.5,
                        fontWeight: 400,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {metric.unit}
                    </Box>
                  ) : null}
                </Typography>
                {showMetricDetail ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.18,
                      color: theme.palette.text.secondary,
                      lineHeight: 1.15,
                      fontWeight: 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {metric.detail}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );

          if (!metricTooltip) {
            return content;
          }

          return (
            <Tooltip key={metric.key} title={metricTooltip}>
              {content}
            </Tooltip>
          );
        })}
      </Box>
    ) : null,
    capacityTags: renderTagRow(model.capacityTags, { singleLine: singleLineCapacityTags }),
    breachTags: model.breachTags.length > 0 ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isCompact ? 0.55 : 0.7 }}>
        {model.breachTags.map((tag) => renderTag(tag))}
      </Box>
    ) : null,
    footer: (model.footerTags.length > 0 || model.footerText) ? (
      <Stack spacing={isCompact ? 0.6 : 0.75}>
        {model.footerTags.length > 0 ? renderTagRow(model.footerTags) : null}
        {model.footerText ? (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: isCompact ? 10.75 : 11.25,
              lineHeight: 1.3,
              letterSpacing: 0,
            }}
          >
            {model.footerText}
          </Typography>
        ) : null}
      </Stack>
    ) : null,
  };

  return (
    <Stack spacing={sectionGap}>
      {(leading || model.topTags.length > 0 || actions) && (
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack
            direction="row"
            spacing={inlineGap}
            alignItems={inlineTitleWithLeading ? 'flex-start' : 'center'}
            sx={{ flexWrap: 'wrap', flex: 1, minWidth: 0 }}
          >
            {leading}
            {inlineTitleWithLeading ? titleBlock : null}
            {model.topTags.map((tag) => renderTag(tag))}
          </Stack>
          {actions ? (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              {actions}
            </Stack>
          ) : null}
        </Stack>
      )}

      {!inlineTitleWithLeading ? titleBlock : null}

      {contentOrder.map((sectionKey) => (
        contentSections[sectionKey] ? <React.Fragment key={sectionKey}>{contentSections[sectionKey]}</React.Fragment> : null
      ))}
    </Stack>
  );
};

export default DenseEntityCardBody;
